/**
 * TEMPORARY back-compat shim for GitLab < 19.2.
 *
 * GA gating is enforced by the backend via the always-sent `X-Gitlab-Client-Name`
 * header, which only exists on GitLab >= 19.2. Older backends self-gate here on the
 * instance's `experiment_features_enabled` setting.
 *
 * To remove once <19.2 support is dropped: delete this file and its test, drop the
 * `ExperimentalFeatureGate` call site + DI wiring in `cli_initialization_service.ts`,
 * and restore a hard `>= MINIMUM_GITLAB_VERSION` floor there. Also delete the
 * `viaExperimentalGate` plumbing: the `InitializationResult.viaExperimentalGate` field
 * (`cli_initialization_service.ts`), the `AgenticChatAccessStatus.experimental` field
 * (`packages/tui/src/types.ts`, set in `tui_controller.ts`), and the
 * `Beta` render branch in `InteractiveModeHeader.tsx`. Unwind the flag from
 * tests by grepping `viaExperimentalGate` / `experimental:`; keep the session-rejection
 * mapping test in `tui_controller.test.ts` — only its flag fixtures go.
 */
import {
  DUO_NAMESPACE_NOT_ENTITLED_MESSAGE,
  DUO_NO_NAMESPACE_DETECTED_MESSAGE,
  GitLabApiService,
  classifyDuoAccessError,
  isFetchError,
  type ApiRequest,
} from '@gitlab-org/core';
import { Logger, withPrefix } from '@gitlab-org/logging';
import { Service, ServiceLifetime } from '@gitlab/needle';
import { coerce, gte } from 'semver';
import { isPreconfiguredCiRun } from './backend/gitlab/gitlab_parsed_options';
import { MINIMUM_GITLAB_VERSION } from './cli_constants';
import { isAllowlistedInstance } from './instance_allowlist';

export type ExperimentalFeatureGateResult =
  | {
      status: 'allowed';
      /**
       * Set when access was granted through one of this shim's own allow branches
       * (CI/DAP exemption, allowlist, experiment-features self-check) rather than the
       * >= 19.2 version check; the TUI header surfaces it as experimental. The CI/DAP
       * exemption precedes the version check, so preconfigured runs against >= 19.2
       * backends also carry the flag — harmless, as headless runs render no header.
       */
      viaExperimentalGate?: true;
    }
  | { status: 'error'; message: string };

interface GroupResponse {
  experiment_features_enabled?: boolean;
}

export interface ExperimentalFeatureGateInput {
  /** Instance version as reported by the API service, if known. */
  instanceVersion?: string;
  /** Base URL of the GitLab instance, used for the <19.2 allowlist bypass. */
  baseUrl?: string;
  /** Root-namespace candidates, in priority order. */
  remoteNamespace?: string;
  defaultNamespacePath?: string;
  /** The CLI command; used to detect genuine CI/DAP preconfigured runs. */
  commandName?: 'run' | 'tui';
}

@Service({
  dependencies: [GitLabApiService, Logger],
  lifetime: ServiceLifetime.Singleton,
})
export class ExperimentalFeatureGate {
  #apiService: GitLabApiService;

  #logger: Logger;

  constructor(apiService: GitLabApiService, logger: Logger) {
    this.#apiService = apiService;
    this.#logger = withPrefix(logger, '[ExperimentalFeatureGate]');
  }

  async check(input: ExperimentalFeatureGateInput): Promise<ExperimentalFeatureGateResult> {
    // Genuine CI/DAP preconfigured runs are exempt from the self-gate. Check this first:
    // in CI the instance version comes from an injected header and is often 'unknown'/
    // unparseable, which would otherwise fall through to the self-gate below.
    if (this.#isPreconfiguredCiRun(input.commandName)) {
      this.#logger.debug('Preconfigured CI/DAP run; skipping experimental-feature gate');
      return { status: 'allowed', viaExperimentalGate: true };
    }

    const { instanceVersion } = input;
    const parsedVersion = coerce(instanceVersion);

    // >= 19.2 is gated by the backend. An unparseable version yields `null` here and
    // must fall through to the self-gate below, so it stays gated rather than being
    // treated as "latest" and allowed.
    if (parsedVersion && gte(parsedVersion, MINIMUM_GITLAB_VERSION)) {
      return { status: 'allowed' };
    }

    // Allowlisted instances (SM < 18.11 that don't expose the setting) bypass the
    // self-check; see `instance_allowlist.ts`.
    if (input.baseUrl && isAllowlistedInstance(input.baseUrl)) {
      this.#logger.debug('Instance is allowlisted; skipping experimental-feature gate');
      return { status: 'allowed', viaExperimentalGate: true };
    }

    return this.#checkExperimentalFeatures(input.remoteNamespace, input.defaultNamespacePath);
  }

  #isPreconfiguredCiRun(commandName?: 'run' | 'tui'): boolean {
    // We pass the env vars directly because `ParsedCliInput` excludes backend options;
    // this covers real CI/DAP, which always injects these vars (the equivalent
    // `--duo-workflow-service-*` flags are not honored here, which is fine for this shim).
    // A local user could export these vars, but doing so is harmless: a bogus token is
    // useless at the service, and >= 19.2 gating is re-enforced by the backend.
    return isPreconfiguredCiRun(commandName, {
      duoWorkflowServiceServer: process.env.DUO_WORKFLOW_SERVICE_SERVER,
      duoWorkflowServiceToken: process.env.DUO_WORKFLOW_SERVICE_TOKEN,
    });
  }

  async #checkExperimentalFeatures(
    remoteNamespace?: string,
    defaultNamespacePath?: string,
  ): Promise<ExperimentalFeatureGateResult> {
    const namespacePath = remoteNamespace || defaultNamespacePath;

    if (!remoteNamespace && defaultNamespacePath) {
      this.#logger.debug(
        `No git remote namespace detected, using user default namespace: ${defaultNamespacePath}`,
      );
    }

    if (!namespacePath) {
      return { status: 'error', message: DUO_NO_NAMESPACE_DETECTED_MESSAGE };
    }

    const rootNamespace = namespacePath.split('/')[0];
    const encodedNamespace = encodeURIComponent(rootNamespace);

    this.#logger.debug(`Checking experimental features for namespace: ${rootNamespace}`);

    let response: GroupResponse;
    try {
      const request: ApiRequest<GroupResponse> = {
        type: 'rest',
        method: 'GET',
        path: `/api/v4/groups/${encodedNamespace}`,
        searchParams: { with_projects: 'false' },
      };
      response = await this.#apiService.fetchFromApi(request);
    } catch (error) {
      this.#logger.error('Failed to fetch group details', error);

      if (isFetchError(error)) {
        const duoError = classifyDuoAccessError(error.status, error.body);
        if (duoError) {
          return { status: 'error', message: duoError.message };
        }
        // The groups API returns 404 (not 403) when the caller can't access the group.
        if (error.status === 404) {
          return { status: 'error', message: DUO_NAMESPACE_NOT_ENTITLED_MESSAGE };
        }
      }

      return {
        status: 'error',
        message:
          `Failed to verify access to GitLab Duo experimental and beta features for group "${rootNamespace}". ` +
          'Ensure you have access to this group and that your GitLab instance is reachable.',
      };
    }

    // Field absent: the backend predates the setting (< 18.11).
    if (response.experiment_features_enabled === undefined) {
      this.#logger.debug(
        `Instance does not expose experimental-features setting: ${rootNamespace}`,
      );
      return {
        status: 'error',
        message:
          'GitLab Duo CLI requires GitLab 18.11 or later. ' +
          'Please ask your administrator to upgrade your GitLab instance.',
      };
    }

    if (response.experiment_features_enabled === false) {
      this.#logger.debug(`Experimental features disabled for namespace: ${rootNamespace}`);
      return {
        status: 'error',
        message:
          'GitLab Duo CLI requires GitLab Duo experimental and beta features to be enabled for your group. ' +
          'Please ask your administrator to enable them under Settings > GitLab Duo.',
      };
    }

    this.#logger.debug(`Experimental features enabled for namespace: ${rootNamespace}`);
    return { status: 'allowed', viaExperimentalGate: true };
  }
}
